import { useState, useRef, useEffect } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiSend, FiChevronDown } from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useSupportTicket, useTicketMessages, addTicketMessage } from '../../hooks/useSupportTickets';
import { updateTicketStatus } from '../../hooks/useAdmin';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { StatusBadge } from '../common/StatusBadge';
import { formatDate, formatRelativeTime } from '../../utils/formatters';

export default function AdminSupportDetail() {
  const { ticketId } = useParams();
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { ticket, loading: ticketLoading } = useSupportTicket(ticketId);
  const { messages, loading: messagesLoading } = useTicketMessages(ticketId);
  const toast = useToast();

  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const adminBg = useColorModeValue('orange.50', 'orange.900');
  const userBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendReply = async () => {
    if (!reply.trim() || !currentUser || !userData) return;
    setSending(true);
    try {
      await addTicketMessage(ticketId, {
        senderUid: currentUser.uid,
        senderName: 'Admin',
        isAdmin: true,
        message: reply.trim(),
      });
      setReply('');
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await updateTicketStatus(ticketId, status);
      toast({ title: `Ticket ${status}`, status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  if (ticketLoading || messagesLoading) return <LoadingSpinner message="Loading ticket..." />;

  if (!ticket) {
    return (
      <VStack spacing={4} py={8}>
        <Heading size="md">Ticket not found</Heading>
        <Button as={Link} to="/admin/support" leftIcon={<Icon as={FiArrowLeft} />}>Back to Support</Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Button
        as={Link}
        to="/admin/support"
        variant="ghost"
        leftIcon={<Icon as={FiArrowLeft} />}
        alignSelf="flex-start"
        size="sm"
      >
        Back to Support
      </Button>

      {/* Ticket header */}
      <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between" align="start">
            <Heading size="md" flex={1}>{ticket.subject}</Heading>
            <HStack spacing={2}>
              <StatusBadge status={ticket.status} />
              <Menu>
                <MenuButton as={Button} size="xs" variant="outline" rightIcon={<FiChevronDown />}>
                  Change Status
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => handleStatusChange('open')}>Open</MenuItem>
                  <MenuItem onClick={() => handleStatusChange('resolved')}>Resolved</MenuItem>
                  <MenuItem onClick={() => handleStatusChange('closed')}>Closed</MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </HStack>
          <HStack spacing={4} fontSize="sm" color="gray.500">
            <Text>From: {ticket.creatorUsername}</Text>
            <Text>Opened {formatDate(ticket.createdAt)}</Text>
          </HStack>
        </VStack>
      </Box>

      {/* Message thread */}
      <VStack spacing={3} align="stretch">
        <MessageBubble
          senderName={ticket.creatorUsername}
          message={ticket.message}
          timestamp={ticket.createdAt}
          isAdmin={false}
          userBg={userBg}
          adminBg={adminBg}
        />

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            senderName={msg.senderName}
            message={msg.message}
            timestamp={msg.createdAt}
            isAdmin={msg.isAdmin}
            userBg={userBg}
            adminBg={adminBg}
          />
        ))}

        <div ref={messagesEndRef} />
      </VStack>

      {/* Reply form */}
      <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} p={4}>
        <VStack spacing={3} align="stretch">
          <Textarea
            placeholder="Reply as admin..."
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                handleSendReply();
              }
            }}
          />
          <HStack justify="space-between" align="center">
            <Text fontSize="xs" color="gray.400">
              Press Cmd+Enter to send
            </Text>
            <Button
              colorScheme="orange"
              size="sm"
              leftIcon={<Icon as={FiSend} />}
              onClick={handleSendReply}
              isLoading={sending}
              isDisabled={!reply.trim()}
            >
              Send as Admin
            </Button>
          </HStack>
        </VStack>
      </Box>
    </VStack>
  );
}

function MessageBubble({ senderName, message, timestamp, isAdmin, userBg, adminBg }) {
  return (
    <Box
      bg={isAdmin ? adminBg : userBg}
      rounded="lg"
      p={4}
      borderWidth={isAdmin ? 1 : 0}
      borderColor={isAdmin ? 'orange.200' : 'transparent'}
    >
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <Avatar size="xs" name={senderName} bg={isAdmin ? 'orange.400' : 'gray.400'} />
          <Text fontSize="sm" fontWeight="semibold">{senderName}</Text>
          {isAdmin && <Badge colorScheme="orange" fontSize="xs">Admin</Badge>}
        </HStack>
        <Text fontSize="xs" color="gray.400">{formatRelativeTime(timestamp)}</Text>
      </HStack>
      <Text fontSize="sm" whiteSpace="pre-wrap" pl={8}>{message}</Text>
    </Box>
  );
}
